import { useMemo, useState, type FormEvent } from 'react';
import { inputStyle } from '@kilat/ui';
import { Timestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useActiveOutlet, type OutletLite } from '../lib/use-active-outlet.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { washPackagesRepoFor } from '../lib/repositories/wash-packages-repo.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX } from '../lib/layout.js';
import {
  excludedOutletIdsFromMap,
  money,
  outletAssignmentMap,
  type WashPackage,
} from '@kilat/shared';

/**
 * /loyalty/packages — operator CRUD for wash subscription packages.
 *
 * Sprint 92 (Phase A): operator defines templates → issues to customers
 * from POS / customer-detail page (Sprint 93+).
 *
 * Sprint 103 (tenant-library migration): packages now live at
 * `tenants/{tid}/washPackages/{id}` (tenant-scoped, NOT per-outlet).
 * Each item carries `excludedOutletIds: string[]` — empty (default) =
 * available at every outlet. Per-outlet visibility is controlled
 * inside the create/edit form via a checkbox grid (all pre-checked).
 * See [[project-tenant-library-catalog]] +
 * `docs/architecture/tenant-library.md`.
 *
 * No-dead-ends: every state explicit
 *   - signed-out → "Sign in"
 *   - no tenant → onboarding
 *   - loading → skeleton
 *   - empty list → call-to-create with the canonical first package
 *   - error → message + reload
 *   - populated → list + Add button
 */
export function LoyaltyPackagesPage() {
  const { user, claims, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const { outlets, loading: outletsLoading } = useActiveOutlet();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const role =
    tenantId && claims !== null
      ? (((claims as { roles?: Record<string, string> }).roles ?? {})[tenantId] ?? null)
      : null;
  const canEdit = role === 'owner' || role === 'manager';

  const sub = useMemo(() => {
    if (!tenantId) return null;
    const repo = washPackagesRepoFor(tenantId);
    return (cb: (vs: WashPackage[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const {
    data: packages,
    loading,
    error,
  } = useTrackedSnapshot<WashPackage[]>(['wash-packages', tenantId], sub);

  const [editing, setEditing] = useState<WashPackage | 'new' | null>(null);

  if (authLoading)
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );
  if (!user)
    return (
      <Shell>
        <EmptyState
          title={t('dashboard.washPackages.signedOutTitle')}
          body={t('dashboard.washPackages.signedOutBody')}
          ctaLabel={t('dashboard.washPackages.signIn')}
          ctaTo="/"
        />
      </Shell>
    );
  if (!tenantId)
    return (
      <Shell>
        <EmptyState
          title={t('dashboard.washPackages.noTenantTitle')}
          body={t('dashboard.washPackages.noTenantBody')}
          ctaLabel={t('dashboard.washPackages.welcomeCta')}
          ctaTo="/welcome"
        />
      </Shell>
    );
  if (outletsLoading)
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );
  if (error)
    return (
      <Shell>
        <EmptyState
          title={t('dashboard.washPackages.loadErrorTitle')}
          body={String(error.message ?? error)}
          ctaLabel={t('dashboard.states.reload')}
          ctaTo="/loyalty/packages"
        />
      </Shell>
    );
  if (loading || packages === null)
    return (
      <Shell>
        <Skeleton />
      </Shell>
    );

  const sorted = packages.slice().sort((a, b) => a.rank - b.rank || a.priceSen - b.priceSen);
  const outletCount = outlets.length;

  return (
    <Shell>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p style={metaEyebrow}>{t('dashboard.washPackages.eyebrow')}</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            {t('dashboard.washPackages.title')}
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 640,
              lineHeight: 1.5,
            }}
          >
            {t('dashboard.washPackages.intro')}
          </p>
        </div>
        {canEdit ? (
          <button type="button" onClick={() => setEditing('new')} style={primaryButton}>
            {t('dashboard.washPackages.newPackage')}
          </button>
        ) : null}
      </header>

      {sorted.length === 0 ? (
        <EmptyState
          title={t('dashboard.washPackages.emptyTitle')}
          body={
            canEdit
              ? t('dashboard.washPackages.emptyBodyCanEdit')
              : t('dashboard.washPackages.emptyBodyReadOnly')
          }
        />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          }}
        >
          {sorted.map((p) => (
            <li key={p.id}>
              <PackageCard
                pkg={p}
                outlets={outlets}
                canEdit={canEdit}
                onEdit={() => setEditing(p)}
              />
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <PackageEditor
          tenantId={tenantId}
          outlets={outlets}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {outletCount === 0 && canEdit ? (
        <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {t('dashboard.washPackages.noOutletsPre')}{' '}
          <Link to="/outlets" style={{ color: 'var(--color-primary)' }}>
            {t('dashboard.washPackages.addOutletLink')}
          </Link>
          .
        </p>
      ) : null}
    </Shell>
  );
}

// ── components ─────────────────────────────────────────────────────────

function PackageCard({
  pkg,
  outlets,
  canEdit,
  onEdit,
}: {
  readonly pkg: WashPackage;
  readonly outlets: readonly OutletLite[];
  readonly canEdit: boolean;
  readonly onEdit: () => void;
}) {
  const { t } = useI18n();
  const headline =
    pkg.name['en'] ?? Object.values(pkg.name)[0] ?? t('dashboard.washPackages.packageFallback');
  const washCount = pkg.washCount ?? 0;
  const kindLabel =
    pkg.kind === 'unlimited'
      ? t('dashboard.washPackages.kindUnlimited')
      : t(
          washCount === 1
            ? 'dashboard.washPackages.washCountOne'
            : 'dashboard.washPackages.washCountOther',
          {
            count: washCount,
          },
        );
  // Sprint 103 — surface assignment status so operators see "available at
  // every outlet" vs "hidden from X" without opening the editor.
  const excludedAtThisTenant = pkg.excludedOutletIds.filter((id) =>
    outlets.some((o) => o.id === id),
  );
  const visibleCount = Math.max(0, outlets.length - excludedAtThisTenant.length);
  const assignmentLabel =
    outlets.length === 0
      ? t('dashboard.washPackages.assignNoOutlets')
      : excludedAtThisTenant.length === 0
        ? outlets.length === 1
          ? t('dashboard.washPackages.assignAllOne')
          : t('dashboard.washPackages.assignAllOther', { count: outlets.length })
        : t('dashboard.washPackages.assignSome', { visible: visibleCount, total: outlets.length });
  return (
    <article
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-3)',
        opacity: pkg.active ? 1 : 0.7,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{headline}</h3>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.washPackages.cardMeta', {
              kind: kindLabel,
              days: pkg.validityDays,
            })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {pkg.featured ? <Chip label={t('dashboard.washPackages.featured')} accent /> : null}
          {!pkg.active ? <Chip label={t('dashboard.washPackages.inactive')} /> : null}
        </div>
      </header>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span
          style={{
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {money.format(pkg.priceSen)}
        </span>
        {pkg.recurringMonths ? (
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            /{' '}
            {t(
              pkg.recurringMonths === 1
                ? 'dashboard.washPackages.perMonthOne'
                : 'dashboard.washPackages.perMonthOther',
              { count: pkg.recurringMonths },
            )}
          </span>
        ) : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            {t('dashboard.washPackages.oneShot')}
          </span>
        )}
      </div>
      {pkg.description ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {pkg.description['en'] ?? Object.values(pkg.description)[0]}
        </p>
      ) : null}
      {pkg.savingsHint ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-primary)',
            fontWeight: 500,
          }}
        >
          {pkg.savingsHint['en'] ?? Object.values(pkg.savingsHint)[0]}
        </p>
      ) : null}
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: excludedAtThisTenant.length === 0 ? 'var(--color-on-surface-muted)' : '#b45309',
        }}
      >
        {assignmentLabel}
      </p>
      {canEdit ? (
        <div style={{ marginTop: 'var(--space-2)', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onEdit} style={ghostButton}>
            {t('dashboard.washPackages.edit')}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function PackageEditor({
  tenantId,
  outlets,
  initial,
  onClose,
}: {
  readonly tenantId: string;
  readonly outlets: readonly OutletLite[];
  readonly initial: WashPackage | null;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const isNew = !initial;
  const [name, setName] = useState(initial?.name['en'] ?? '');
  const [description, setDescription] = useState(initial?.description?.['en'] ?? '');
  const [kind, setKind] = useState<'count' | 'unlimited'>(initial?.kind ?? 'count');
  const [washCount, setWashCount] = useState(String(initial?.washCount ?? 5));
  const [validityDays, setValidityDays] = useState(String(initial?.validityDays ?? 90));
  const [priceRm, setPriceRm] = useState(String((initial?.priceSen ?? 5000) / 100));
  const [recurringMonths, setRecurringMonths] = useState(
    initial?.recurringMonths ? String(initial.recurringMonths) : '',
  );
  const [featured, setFeatured] = useState(initial?.featured ?? false);
  const [active, setActive] = useState(initial?.active ?? true);
  const [savingsHint, setSavingsHint] = useState(initial?.savingsHint?.['en'] ?? '');

  // Sprint 103 — per-outlet checkbox state. Pre-checked for every outlet
  // by default (or restored from existing `excludedOutletIds` when editing).
  const outletIds = useMemo(() => outlets.map((o) => o.id), [outlets]);
  const [outletAssignment, setOutletAssignment] = useState<Record<string, boolean>>(() =>
    outletAssignmentMap(initial?.excludedOutletIds, outletIds),
  );

  const [stage, setStage] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  function toggleOutletAssignment(outletId: string) {
    setOutletAssignment((prev) => ({ ...prev, [outletId]: !(prev[outletId] ?? true) }));
  }

  const visibleAtCount = Object.values(outletAssignment).filter((v) => v).length;
  const allUnchecked = outlets.length > 0 && visibleAtCount === 0;

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setStage({ kind: 'saving' });
    try {
      const repo = washPackagesRepoFor(tenantId);
      const priceSen = Math.max(0, Math.round(Number(priceRm) * 100));
      const washCountInt = Math.max(1, Math.floor(Number(washCount)));
      const validityInt = Math.max(0, Math.floor(Number(validityDays)));
      if (kind === 'count' && !(washCountInt > 0)) {
        setStage({ kind: 'error', message: t('dashboard.washPackages.errWashCount') });
        return;
      }
      if (allUnchecked) {
        setStage({
          kind: 'error',
          message: t('dashboard.washPackages.errPickOutlet'),
        });
        return;
      }
      const excludedOutletIds = excludedOutletIdsFromMap(outletAssignment, outletIds);
      const now = Timestamp.now();
      // crypto.randomUUID gives a stable 36-char id without needing
      // to expose a Firestore CollectionReference on the repo surface.
      const id = initial?.id ?? `wp-${crypto.randomUUID().slice(0, 12)}`;
      await repo.set(id, {
        id,
        excludedOutletIds,
        name: { en: name.trim(), ms: name.trim(), zh: name.trim(), ta: name.trim() },
        ...(description.trim()
          ? {
              description: {
                en: description.trim(),
                ms: description.trim(),
                zh: description.trim(),
                ta: description.trim(),
              },
            }
          : {}),
        kind,
        ...(kind === 'count' ? { washCount: washCountInt } : {}),
        validityDays: validityInt,
        priceSen,
        ...(recurringMonths.trim()
          ? { recurringMonths: Math.max(1, Math.floor(Number(recurringMonths))) }
          : {}),
        eligibleServiceIds: initial?.eligibleServiceIds ?? [],
        eligibleVehicleTypes: initial?.eligibleVehicleTypes ?? [],
        featured,
        active,
        ...(savingsHint.trim()
          ? {
              savingsHint: {
                en: savingsHint.trim(),
                ms: savingsHint.trim(),
                zh: savingsHint.trim(),
                ta: savingsHint.trim(),
              },
            }
          : {}),
        rank: initial?.rank ?? 0,
        createdAt: initial?.createdAt ?? now,
        updatedAt: now,
      });
      onClose();
    } catch (e) {
      logger.error('wash package save failed', { error: String(e) });
      setStage({
        kind: 'error',
        message: String((e as Error).message ?? t('dashboard.washPackages.errCouldNotSave')),
      });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 50,
        padding: 'var(--space-4)',
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          maxWidth: 560,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-3)',
          display: 'grid',
          gap: 'var(--space-4)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {isNew
            ? t('dashboard.washPackages.editorNewTitle')
            : t('dashboard.washPackages.editorEditTitle')}
        </h2>
        <label style={fieldStack}>
          <span style={fieldLabel}>{t('dashboard.washPackages.nameLabel')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('dashboard.washPackages.namePlaceholder')}
            required
            style={inputStyle}
          />
        </label>
        <label style={fieldStack}>
          <span style={fieldLabel}>{t('dashboard.washPackages.descLabel')}</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('dashboard.washPackages.descPlaceholder')}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          }}
        >
          <label style={fieldStack}>
            <span style={fieldLabel}>{t('dashboard.washPackages.typeLabel')}</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as 'count' | 'unlimited')}
              style={inputStyle}
            >
              <option value="count">{t('dashboard.washPackages.typeCount')}</option>
              <option value="unlimited">{t('dashboard.washPackages.typeUnlimited')}</option>
            </select>
          </label>
          {kind === 'count' ? (
            <label style={fieldStack}>
              <span style={fieldLabel}>{t('dashboard.washPackages.washCountLabel')}</span>
              <input
                value={washCount}
                onChange={(e) => setWashCount(e.target.value)}
                inputMode="numeric"
                style={inputStyle}
              />
            </label>
          ) : (
            <div />
          )}
          <label style={fieldStack}>
            <span style={fieldLabel}>{t('dashboard.washPackages.priceLabel')}</span>
            <input
              value={priceRm}
              onChange={(e) => setPriceRm(e.target.value)}
              inputMode="decimal"
              style={inputStyle}
            />
          </label>
          <label style={fieldStack}>
            <span style={fieldLabel}>{t('dashboard.washPackages.validityLabel')}</span>
            <input
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
              inputMode="numeric"
              style={inputStyle}
            />
          </label>
          <label style={fieldStack}>
            <span style={fieldLabel}>{t('dashboard.washPackages.autoRenewLabel')}</span>
            <input
              value={recurringMonths}
              onChange={(e) => setRecurringMonths(e.target.value)}
              inputMode="numeric"
              placeholder="1"
              style={inputStyle}
            />
          </label>
        </div>
        <label style={fieldStack}>
          <span style={fieldLabel}>{t('dashboard.washPackages.savingsHintLabel')}</span>
          <input
            value={savingsHint}
            onChange={(e) => setSavingsHint(e.target.value)}
            placeholder={t('dashboard.washPackages.savingsHintPlaceholder')}
            style={inputStyle}
          />
        </label>

        {outlets.length > 1 ? (
          <fieldset
            style={{
              border: '1px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            <legend style={{ ...fieldLabel, padding: '0 var(--space-2)' }}>
              {t('dashboard.washPackages.availableAtLegend')}
            </legend>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                lineHeight: 1.5,
              }}
            >
              {t('dashboard.washPackages.availableAtHint')}
            </p>
            {outlets.map((o) => {
              const checked = outletAssignment[o.id] ?? true;
              return (
                <label
                  key={o.id}
                  style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOutletAssignment(o.id)}
                  />
                  <span style={{ fontSize: 'var(--text-meta)' }}>{o.name}</span>
                </label>
              );
            })}
            {allUnchecked ? (
              <p role="alert" style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#b45309' }}>
                {t('dashboard.washPackages.errPickOutlet')}
              </p>
            ) : null}
          </fieldset>
        ) : outlets.length === 1 && outlets[0] ? (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t('dashboard.washPackages.singleOutletNote', { name: outlets[0].name })}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
            />
            <span style={{ fontSize: 'var(--text-meta)' }}>
              {t('dashboard.washPackages.featuredCheckbox')}
            </span>
          </label>
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span style={{ fontSize: 'var(--text-meta)' }}>
              {t('dashboard.washPackages.activeCheckbox')}
            </span>
          </label>
        </div>
        {stage.kind === 'error' ? (
          <p role="alert" style={errorStyle}>
            {stage.message}
          </p>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <button type="button" onClick={onClose} style={ghostButton}>
            {t('dashboard.washPackages.cancel')}
          </button>
          <button
            type="submit"
            disabled={stage.kind === 'saving' || !name.trim()}
            style={{
              ...primaryButton,
              opacity: stage.kind === 'saving' || !name.trim() ? 0.55 : 1,
              cursor: stage.kind === 'saving' ? 'wait' : 'pointer',
            }}
          >
            {stage.kind === 'saving'
              ? t('dashboard.washPackages.saving')
              : isNew
                ? t('dashboard.washPackages.createPackage')
                : t('dashboard.washPackages.saveChanges')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── shell + helpers ────────────────────────────────────────────────────

function Shell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-6) var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
        maxWidth: PAGE_FRAME_MAX,
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

function Skeleton() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }} aria-busy="true">
      <div
        style={{ height: 60, borderRadius: 'var(--radius-md)', background: 'var(--color-border)' }}
      />
      <div
        style={{
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 180,
              borderRadius: 'var(--radius-lg)',
              background: 'var(--color-border)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState({
  title,
  body,
  ctaLabel,
  ctaTo,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaTo?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-8) var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 460,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {ctaLabel && ctaTo ? (
        <Link
          to={ctaTo}
          style={{ ...primaryButton, marginTop: 'var(--space-4)', display: 'inline-flex' }}
        >
          {ctaLabel}
        </Link>
      ) : null}
    </section>
  );
}

function Chip({ label, accent }: { readonly label: string; readonly accent?: boolean }) {
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: accent ? 'rgba(13,122,79,0.10)' : 'var(--color-border)',
        color: accent ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
        fontSize: 'var(--text-meta)',
        fontWeight: 500,
      }}
    >
      {label}
    </span>
  );
}

const metaEyebrow: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  letterSpacing: 4,
  textTransform: 'uppercase',
};

const fieldStack: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)' };
const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};
const primaryButton: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '0.5em 1em',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
  boxShadow: 'var(--shadow-1)',
  textDecoration: 'none',
};
const ghostButton: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '0.5em 1em',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};
const errorStyle: React.CSSProperties = {
  margin: 0,
  padding: '0.5em 0.75em',
  background: 'rgba(239,68,68,0.06)',
  border: '1px solid rgba(239,68,68,0.22)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  color: '#b91c1c',
};
